import {WEEK_LENGTH} from '@wix/wix-events-commons-statics'
import classNames from 'classnames'
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {isEditor as isEditorSelector} from '../../../../../../../../commons/selectors/environment'
import {
  useWidgetActions,
  useWidgetHost,
  useWidgetRuntimeContext,
  useWidgetState,
} from '../../../../../hooks/state-provider'
import {
  getEventDetailsStyleHash,
  getGridCellLocation,
  getSelectedDate,
  getSelectedEventId,
} from '../../../../../selectors/calendar-layout'
import {CALENDAR_WEEKS, getPopupPlacement} from '../../../../../utils/calendar-layout-popup'
import {MonthlyCalendarEventDetails} from './event-details'
import {MonthlyCalendarEventList} from './event-list'
import {MonthlyCalendarPopupProps} from './interfaces'
import s from './monthly-calendar-popup.scss'
import {Triangle} from './triangle'
import {TriangleDirection} from './triangle/interfaces'

export const MonthlyCalendarPopup = ({getCellSizes}: MonthlyCalendarPopupProps) => {
  const [contentHeight, setContentHeight] = useState(0)
  const popupElementRef = useRef<HTMLDivElement>(null)

  const {closeMonthlyCalendarPopup} = useWidgetActions()

  const selectedEventId = useWidgetState(getSelectedEventId)
  const selectedDate = useWidgetState(getSelectedDate)
  const open = Boolean(selectedDate)
  const {weekIndex: week, weekDayIndex} = useWidgetState(state => getGridCellLocation(state, selectedDate))
  const editor = useWidgetState(isEditorSelector)

  const host = useWidgetHost()
  const popupStyleHash = editor && getEventDetailsStyleHash({host})
  const {isRTL: rtl} = useWidgetRuntimeContext()

  const ready =
    open &&
    Boolean(contentHeight) &&
    Object.keys(getCellSizes()).length === CALENDAR_WEEKS &&
    Object.keys(getCellSizes()[CALENDAR_WEEKS - 1]).length === WEEK_LENGTH

  const popupPlacement = ready ? getPopupPlacement(getCellSizes(), contentHeight, week, weekDayIndex, rtl) : null

  const style = ready
    ? Object.entries(popupPlacement.styles).reduce((styles, [rule, value]) => {
        styles[rule] = `calc(${value} * 1px)`
        return styles
      }, {})
    : {}

  const triangle = popupPlacement ? popupPlacement.triangle : {direction: TriangleDirection.left, top: 0}

  useEffect(() => {
    if (!editor && open) {
      const firstFocusableElement = popupElementRef.current?.querySelector(
        "[data-hook^='calendar-popup-close-button']",
      ) as HTMLElement

      if (firstFocusableElement) {
        setTimeout(() => firstFocusableElement.focus())
      }
    }
  }, [open, selectedEventId])

  useLayoutEffect(() => {
    if (open && !contentHeight && popupElementRef.current) {
      setContentHeight(popupElementRef.current.clientHeight)
    }
  }, [open, contentHeight])

  useEffect(() => {
    if (open && contentHeight) {
      setContentHeight(0)
    }
  }, [selectedEventId, popupStyleHash])

  useEffect(() => {
    if (!open) {
      setContentHeight(0)
    }
  }, [open])

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeMonthlyCalendarPopup()
    }
  }

  if (!open) {
    return null
  }

  return (
    <div
      ref={popupElementRef}
      style={style}
      className={classNames(s.popup, {[s.visible]: ready, [s.editor]: editor})}
      onKeyUp={handleKeyUp}
      data-hook="calendar-popup"
    >
      <div className={s.relativeContainer}>
        <div className={s.content}>
          {selectedEventId ? <MonthlyCalendarEventDetails /> : <MonthlyCalendarEventList />}
        </div>
        <div className={classNames(s.triangle, s[triangle.direction])} style={{top: `calc(${triangle.top} * 1px)`}}>
          <Triangle direction={TriangleDirection[triangle.direction]} />
        </div>
      </div>
    </div>
  )
}
